import { ColumnType, Pagination, Table } from "@/components";
import DateRangePicker from "@/components/DateRangePicker";
import { storeInspectionService } from "@/services/storeInspectionService";
import { ApiResponse, DEFAULT_ERROR_MESSAGE } from "@/types";
import { formatDateCustom, toastError } from "@/utils";
import { useEffect, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaFilePdf } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import api from "@/services/api";

const KEYS_SEARCH_PARAMS = {
  PAGE: "page",
  DATE_FROM: "date_from",
  DATE_TO: "date_to",
};

// --- Types ---
type StoreInspectionResult = {
  id: number;
  survey_date: string;
  sv_name: string;
  store_name: string;
  pdf: { name: string; type: string };
};

// --- Page ---
const StoreInspectionResultsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;
  const dateFrom = params[KEYS_SEARCH_PARAMS.DATE_FROM] || "";
  const dateTo = params[KEYS_SEARCH_PARAMS.DATE_TO] || "";

  const [results, setResults] = useState<ApiResponse<StoreInspectionResult[]>>({
    data: [],
    status: 200,
  });
  const [loading, setLoading] = useState(false);
  const [loadingPdfId, setLoadingPdfId] = useState<number | null>(null);

  const updateSearchParams = (object: Record<string, string | undefined>) => {
    const next = { ...params };
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) next[key] = value;
      else delete next[key];
    }
    setSearchParams(next, { replace: true });
  };

  const handlePreviewPdf = async (record: StoreInspectionResult) => {
    if (loadingPdfId === record.id) return;
    setLoadingPdfId(record.id);
    try {
      const response = await api.get(
        `/store/my-store-inspections/${record.id}/pdf`,
        { responseType: "blob", headers: { Accept: "application/pdf" } },
      );
      const pdfBlob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank", "noopener,noreferrer");

      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
    } catch (error: any) {
      toastError(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoadingPdfId(null);
    }
  };

  const columns: ColumnType<StoreInspectionResult>[] = [
    {
      title: "巡回日",
      dataIndex: "survey_date",
      key: "survey_date",
      width: "30%",
      render: (v) => (
        <p className="truncate">
          {v ? formatDateCustom(v, "YYYY年MM月DD日") : "-"}
        </p>
      ),
    },
    {
      title: "巡回結果 (PDF)",
      dataIndex: "pdf",
      key: "pdf",
      width: "70%",
      render: (pdf, record) => {
        const isLoadingPdf = loadingPdfId === record.id;

        return pdf?.name ? (
          <button
            type="button"
            className="flex items-center gap-2 text-other_social_linkedin hover:underline hover:opacity-80 disabled:opacity-70 disabled:cursor-wait"
            onClick={() => handlePreviewPdf(record)}
            disabled={isLoadingPdf}
          >
            {isLoadingPdf ? (
              <AiOutlineLoading3Quarters className="animate-spin size-4 shrink-0" />
            ) : (
              <FaFilePdf className="size-4 shrink-0 text-red-500" />
            )}
            <span className="truncate">
              {isLoadingPdf ? "読み込み中..." : pdf.name}
            </span>
          </button>
        ) : (
          <p>-</p>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    storeInspectionService
      .getStoreInspectionResults({
        page: currentPage,
        limit: 14,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      })
      .then(setResults)
      .catch((error: any) => {
        toastError(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  }, [currentPage, dateFrom, dateTo]);

  const pagination = results.pagination;
  const hasPagination =
    results.data.length > 0 && (pagination?.pageCount || 0) > 0;

  return (
    <div>
      <section className="flex flex-col h-[5rem] gap-1 justify-center">
        <h1 className="text-lg font-bold leading-7 whitespace-nowrap">
          店舗巡回結果
        </h1>
        <p className="text-light_secondary font-normal text-xs leading-4.5">
          店舗巡回の結果をこちらから確認できます。
        </p>
      </section>
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <DateRangePicker
          containerClassName="relative"
          value={{ start: dateFrom, end: dateTo }}
          onChange={(range) =>
            updateSearchParams({
              [KEYS_SEARCH_PARAMS.DATE_FROM]: range.start
                ? formatDateCustom(range.start, "YYYY-MM-DD")
                : undefined,
              [KEYS_SEARCH_PARAMS.DATE_TO]: range.end
                ? formatDateCustom(range.end, "YYYY-MM-DD")
                : undefined,
              [KEYS_SEARCH_PARAMS.PAGE]: "1",
            })
          }
        />
      </div>

      <div className="pt-4">
        <div className="min-h-[calc(100vh-478px)]">
          <Table
            columns={columns}
            dataSource={results.data}
            loading={loading}
            bordered
            loadingClassname="h-[calc(100vh-530px)]"
          />
        </div>
        {hasPagination && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-auro_metal_saurus whitespace-nowrap">
              {results.data.length}/{pagination?.itemCount || 0}件
            </p>
            <Pagination
              currentPage={currentPage}
              totalPages={pagination?.pageCount || 0}
              onPageChange={(page) =>
                updateSearchParams({
                  [KEYS_SEARCH_PARAMS.PAGE]: page.toString(),
                })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { StoreInspectionResultsList };
